<script setup>
import { onMounted, ref, computed } from "vue";
import { useRouter } from "vue-router";
import { useTherapistStore } from "@/stores/therapist";
import WorkingHoursMassUpdateDialog from "@/modules/Therapists/WokingHours/Components/WorkingHoursMassUpdateDialog.vue";

const store = useTherapistStore();
const router = useRouter();
const searchQuery = ref("");

const actions = ref([
    {
        apiName: "SetWorkingHours",
        title: "Set Working Hours",
        isOpenDialog: false,
    }
]);

const filteredTherapists = computed(() => {
    if (!searchQuery.value) return store.therapists;

    const query = searchQuery.value.toLowerCase();
    return store.therapists.filter((therapist) =>
        therapist.first_name?.toLowerCase()?.includes(query) ||
    therapist.last_name?.toLowerCase()?.includes(query) ||
    therapist.email?.toLowerCase()?.includes(query)
    );
});

function handleTherapistClick(therapist) {
    router.push({
        name: "therapist-details",
        params: { therapistId: therapist.ROWID }
    });
}

function handleAction(action) {
    action.isOpenDialog = true;
}

function clearSearch() {
    searchQuery.value = "";
}

onMounted(async () => {
    if (!store.therapists.length) {
        await store.fetchTherapists();
    }
});
</script>

<template>
  <working-hours-mass-update-dialog
      v-model="actions[0].isOpenDialog"
  />
  <v-container>
    <div class="bg-white rounded-lg shadow p-6">
      <div class="flex justify-between items-center mb-6">
        <div class="flex flex-col">
          <h2 class="text-xl font-semibold">Therapists</h2>
          <p class="text-sm text-gray-500">Select a therapist to view their details</p>
        </div>

        <div class="space-x-2">
          <div class="d-flex justify-space-around">
            <v-btn color="primary">
              Actions
              <v-menu activator="parent">
                <v-list>
                  <v-list-item
                      v-for="action in actions"
                      :key="action.apiName"
                      :value="action.apiName"
                  >
                    <v-list-item-title @click="handleAction(action)">{{ action.title }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Sticky Search Bar -->
      <div class="sticky top-0 z-10 bg-white py-3 border-b shadow-sm">
        <div class="relative">
          <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
            </svg>
          </div>
          <input
            type="text"
            v-model="searchQuery"
            class="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-all"
            placeholder="Search therapists by name or email..."
          />
          <div
            v-if="searchQuery"
            @click="clearSearch"
            class="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer"
          >
            <svg class="w-5 h-5 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </div>
        </div>
        <div class="mt-2 text-sm text-gray-500 flex items-center">
          <span>{{ filteredTherapists.length }} therapist{{ filteredTherapists.length !== 1 ? 's' : '' }} found</span>
        </div>
      </div>

      <div class="p-4">
        <div v-if="store.loading" class="text-center py-8">
          <div class="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          <p class="mt-2">Loading therapists...</p>
        </div>
        <div v-else-if="filteredTherapists.length === 0" class="text-center py-12">
          <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9
            10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <p class="mt-2 text-gray-500">No therapists found matching "{{ searchQuery }}"</p>
          <button @click="clearSearch" class="mt-3 text-blue-500 hover:text-blue-700">Clear search</button>
        </div>
        <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div
              v-for="therapist in filteredTherapists"
              :key="therapist.ROWID"
              @click="handleTherapistClick(therapist)"
              class="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors hover:shadow-md"
          >
            <div class="font-medium">{{ therapist.first_name }} {{ therapist.last_name }}</div>
            <div class="text-xs text-gray-400 mt-1">{{ therapist.email }}</div>
          </div>
        </div>
      </div>
    </div>
  </v-container>
</template>

<style scoped>
.sticky {
  position: sticky;
  backdrop-filter: blur(8px);
  background-color: rgba(255, 255, 255, 0.95);
}
</style>
